import { useEffect, useState, useCallback } from "react";
import { vaultApi, venturesApi, auditorApi, vaultScanApi } from "@/lib/api-client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Unlock, Upload, FileText, Trash2, ShieldAlert, Clock, Skull, KeyRound, ScanSearch, RefreshCw, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { RISK_META, relativeTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { authApi } from "@/lib/api-client";
import BackgroundTexture from "@/assets/vault-texture.js";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function PrivateVault() {
  const { user, patchUser } = useAuth();
  const [pinInput, setPinInput] = useState("");
  const [vaultToken, setVaultToken] = useState(null);
  const [locked, setLocked] = useState(true);
  const [docs, setDocs] = useState([]);
  const [archivistMode, setArchivistMode] = useState(true);  // Vault as Archivist — default to Executed only
  const [shakeKey, setShakeKey] = useState(0);
  const [dmStatus, setDmStatus] = useState(null);
  const [ventures, setVentures] = useState([]);
  const [attachVentureId, setAttachVentureId] = useState("");
  const [contact, setContact] = useState({
    name: user?.dead_man_contact_name || "",
    email: user?.dead_man_contact_email || "",
    days: user?.dead_man_days || 30,
    beneficiary_message: user?.beneficiary_message || "",
  });
  const [pinSetupMode, setPinSetupMode] = useState(!user?.vault_pin_set);

  const loadStatus = useCallback(async () => {
    try {
      const s = await vaultApi.status();
      setDmStatus(s);
    } catch (e) { console.error("vault status failed", e); }
  }, []);

  useEffect(() => {
    loadStatus();
    venturesApi.list().then(setVentures).catch((e) => console.error("ventures load failed", e));
  }, [loadStatus]);

  const setPin = async () => {
    if (pinInput.length < 4) return toast.error("PIN must be 4+ digits");
    try {
      await vaultApi.setPin(pinInput);
      patchUser({ vault_pin_set: true });
      toast.success("Vault PIN secured");
      setPinSetupMode(false);
      setPinInput("");
    } catch { toast.error("Failed to set PIN"); }
  };

  const unlock = async () => {
    try {
      const r = await vaultApi.verifyPin(pinInput);
      setVaultToken(r.vault_token);
      setLocked(false);
      setPinInput("");
      const d = await vaultApi.list(r.vault_token);
      setDocs(d);
      toast.success("Vault unlocked");
    } catch {
      setShakeKey((k) => k + 1);
      toast.error("Invalid PIN");
    }
  };

  const lock = () => {
    setLocked(true);
    setVaultToken(null);
    setDocs([]);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5_000_000) return toast.error("Max 5MB per file");
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result).split(",")[1] || "";
      try {
        const result = await vaultApi.upload(vaultToken, {
          name: file.name,
          mime: file.type || "application/octet-stream",
          content_b64: b64,
          venture_id: attachVentureId || null,
        });
        const risk = result?.audit?.risk_level || "green";
        if (risk === "red") toast.error(`Auditor: RED risk on ${file.name}`);
        else if (risk === "yellow") toast.warning(`Auditor: Watch — ${file.name}`);
        else toast.success(`Sealed & audited: ${file.name}`);
        const d = await vaultApi.list(vaultToken);
        setDocs(d);
      } catch { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const rescan = async (doc) => {
    try {
      toast.info(`Auditor re-scanning ${doc.name}…`);
      await auditorApi.rescan(doc.id, vaultToken);
      const d = await vaultApi.list(vaultToken);
      setDocs(d);
      toast.success("Re-scan complete");
    } catch { toast.error("Rescan failed"); }
  };

  const [scanning, setScanning] = useState(false);
  const handleCamera = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5_000_000) return toast.error("Image too large (max 5MB)");
    setScanning(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result).split(",")[1] || "";
      try {
        const r = await vaultScanApi.scan(vaultToken, file.name, file.type || "image/jpeg", b64, attachVentureId || null);
        const risk = r?.audit?.risk_level || "green";
        const chars = (r?.extracted_text || "").length;
        if (risk === "red") toast.error(`Scanned: ${chars} chars · RED risk detected`);
        else toast.success(`Scanned & sealed: ${chars} chars extracted · ${risk}`);
        const d = await vaultApi.list(vaultToken);
        setDocs(d);
      } catch { toast.error("Scan failed"); }
      finally { setScanning(false); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const download = async (doc) => {
    try {
      const full = await vaultApi.get(doc.id, vaultToken);
      if (!full.content_b64) {
        toast.error("This artifact has no downloadable content stored");
        return;
      }
      const bin = atob(full.content_b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: full.mime || "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = full.name || `${doc.title || "vault-document"}.txt`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Download failed");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.name}?`)) return;
    try {
      await vaultApi.remove(doc.id, vaultToken);
      setDocs(docs.filter((d) => d.id !== doc.id));
      toast.success("Removed from vault");
    } catch { toast.error("Delete failed"); }
  };

  const saveDeadMan = async () => {
    try {
      await authApi.updateSettings({
        dead_man_contact_name: contact.name,
        dead_man_contact_email: contact.email,
        dead_man_days: Number(contact.days) || 30,
        beneficiary_message: contact.beneficiary_message || "",
      });
      patchUser({
        dead_man_contact_name: contact.name,
        dead_man_contact_email: contact.email,
        dead_man_days: Number(contact.days) || 30,
        beneficiary_message: contact.beneficiary_message || "",
      });
      toast.success("Dead Man's Switch configured");
      await loadStatus();
    } catch (e) { console.error("dms save failed", e); toast.error("Save failed"); }
  };

  const checkIn = async () => {
    try {
      await vaultApi.checkin();
      toast.success("Presence confirmed. Timer reset.");
      await loadStatus();
    } catch { toast.error("Check-in failed"); }
  };

  return (
    <section
      className="uc-card p-6 uc-fade relative overflow-hidden"
      data-testid="private-vault"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.85), rgba(10,10,10,0.95)), url(${BackgroundTexture})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <div className="uc-chip mb-2"><Lock size={11}/> PRIVATE VAULT · DOUBLE SECURED</div>
          <h2 className="font-serif-display text-2xl">The Sealed Chamber</h2>
          <p className="text-white/50 text-xs mt-1">Sensitive documents. Independent PIN required.</p>
        </div>
        {!locked && (
          <button data-testid="lock-vault-btn" onClick={lock} className="uc-chip hover:bg-white/5 transition-colors">
            <Lock size={11}/> Lock Now
          </button>
        )}
      </div>

      {pinSetupMode ? (
        <div className="p-5 border border-amber-500/30 bg-black/60" data-testid="vault-pin-setup">
          <div className="flex items-center gap-2 mb-3 text-amber-400">
            <KeyRound size={14}/><span className="font-mono-uc text-[11px] uppercase tracking-widest">Initialize Vault PIN</span>
          </div>
          <p className="text-white/60 text-sm mb-4">Set a private PIN (4-12 characters) — separate from your login password.</p>
          <div className="flex gap-2">
            <Input
              data-testid="vault-setup-pin-input"
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              placeholder="Enter new PIN"
              className="h-11 rounded-none bg-black border-white/10 font-mono-uc"
            />
            <Button data-testid="vault-setup-pin-btn" onClick={setPin} className="uc-btn-amber rounded-none h-11">Secure Vault</Button>
          </div>
        </div>
      ) : locked ? (
        <div key={shakeKey} className="p-5 border border-white/10 bg-black/60 uc-shake" data-testid="vault-locked">
          <div className="flex items-center gap-2 mb-3 text-white/70">
            <Lock size={14}/><span className="font-mono-uc text-[11px] uppercase tracking-widest">Vault Sealed</span>
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="vault-pin-input"
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && unlock()}
              placeholder="Enter Vault PIN"
              className="h-11 rounded-none bg-black border-white/10 font-mono-uc"
            />
            <Button data-testid="vault-unlock-btn" onClick={unlock} className="uc-btn-amber rounded-none h-11">
              <Unlock size={14} className="mr-2"/> Unlock
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="vault-unlocked">
          <div className="flex items-center gap-2">
            <select
              data-testid="vault-attach-venture"
              value={attachVentureId}
              onChange={(e) => setAttachVentureId(e.target.value)}
              className="flex-1 h-9 rounded-none bg-black border border-white/10 text-white/80 text-xs px-2 uc-focus font-mono-uc"
            >
              <option value="">— Attach to venture (optional) —</option>
              {ventures.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
          <label className="p-4 border border-dashed border-white/20 hover:border-amber-500/50 bg-black/40 flex items-center gap-3 cursor-pointer transition-colors group" data-testid="vault-upload-zone">
            <input type="file" onChange={handleFile} className="hidden" data-testid="vault-upload-input"/>
            <Upload size={16} className="text-white/40 group-hover:text-amber-400 transition-colors"/>
            <div className="flex-1">
              <div className="text-sm text-white/80">Seal a new document {attachVentureId && <span className="text-amber-400 text-xs ml-2">→ {ventures.find(v => v.id === attachVentureId)?.name}</span>}</div>
              <div className="text-[11px] font-mono-uc text-white/40">Max 5MB · Auditor scans automatically</div>
            </div>
          </label>

          <label className="p-4 border border-dashed border-blue-500/30 hover:border-blue-400/60 bg-blue-500/[0.03] flex items-center gap-3 cursor-pointer transition-colors group" data-testid="vault-camera-zone">
            <input type="file" accept="image/*" capture="environment" onChange={handleCamera} className="hidden" data-testid="vault-camera-input"/>
            {scanning ? <Loader2 size={16} className="animate-spin text-blue-400"/> : <Camera size={16} className="text-blue-400 group-hover:text-blue-300 transition-colors"/>}
            <div className="flex-1">
              <div className="text-sm text-white/80">{scanning ? "Extracting text…" : "Scan document with camera"}</div>
              <div className="text-[11px] font-mono-uc text-white/40">Mobile-optimized · Directorate OCR via Claude Vision · Auditor scans extracted text</div>
            </div>
          </label>

          <div className="flex items-center justify-between mb-2 pt-2 border-t border-white/5">
            <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50">
              {archivistMode ? "Executed Archive" : "All Documents"}
            </div>
            <div className="flex gap-1">
              <button
                data-testid="vault-tab-executed"
                onClick={() => setArchivistMode(true)}
                className={`px-2 h-6 text-[10px] font-mono-uc uppercase tracking-widest border transition-colors ${archivistMode ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-white/10 text-white/50 hover:text-white/80"}`}
              >Executed</button>
              <button
                data-testid="vault-tab-all"
                onClick={() => setArchivistMode(false)}
                className={`px-2 h-6 text-[10px] font-mono-uc uppercase tracking-widest border transition-colors ${!archivistMode ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-white/10 text-white/50 hover:text-white/80"}`}
              >All</button>
            </div>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto uc-scroll" data-testid="vault-docs-list">
            {docs.filter((d) => archivistMode ? d.category === "executed" : true).map((d) => {
              const risk = d.audit?.risk_level || "green";
              const rm = RISK_META[risk] || RISK_META.green;
              const findings = d.audit?.findings || [];
              return (
                <div key={d.id} className="flex items-center justify-between p-2.5 border border-white/5 hover:border-white/15 bg-black/40 transition-colors group" data-testid={`vault-doc-${d.id}`}
                     style={{ borderLeft: `2px solid ${rm.color}` }}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText size={13} className="text-amber-400 flex-shrink-0"/>
                    <div className="min-w-0">
                      <div className="text-sm text-white/90 truncate">{d.name}</div>
                      <div className="font-mono-uc text-[10px] text-white/40 flex items-center gap-2">
                        <span className="uc-dot" style={{ background: rm.color }}></span>
                        <span data-testid={`vault-doc-risk-${d.id}`}>Audit: {rm.label}</span>
                        {findings.length > 0 && <span>· {findings.length} finding{findings.length !== 1 ? "s" : ""}</span>}
                        <span>· {relativeTime(d.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    {findings.length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button data-testid={`vault-audit-${d.id}`} className="text-[10px] font-mono-uc uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: rm.color, color: rm.color }}>
                            {rm.label}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 bg-[#0F0F0F] border-white/10 text-white p-3" side="left">
                          <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 mb-2">Auditor Findings</div>
                          <div className="space-y-2 max-h-64 overflow-y-auto uc-scroll">
                            {findings.map((f, i) => (
                              <div key={`${d.id}-finding-${i}-${f.category || ""}`} className="p-2 border border-white/10 text-xs">
                                <div className="font-mono-uc text-[9px] uppercase text-white/50">{f.category?.replace(/_/g, " ")}</div>
                                <div className="text-white/80 mt-1">{f.explanation}</div>
                                {f.excerpt && <div className="text-white/50 italic mt-1 text-[11px]">"{f.excerpt.slice(0, 200)}"</div>}
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button data-testid={`vault-rescan-${d.id}`} onClick={() => rescan(d)} className="text-white/40 hover:text-amber-400" title="Re-scan">
                        <RefreshCw size={11}/>
                      </button>
                      <button data-testid={`vault-download-${d.id}`} onClick={() => download(d)} className="text-white/50 hover:text-amber-400 text-[11px] font-mono-uc uppercase">Open</button>
                      <button data-testid={`vault-delete-${d.id}`} onClick={() => remove(d)} className="text-white/40 hover:text-red-400"><Trash2 size={12}/></button>
                    </div>
                  </div>
                </div>
              );
            })}
            {docs.length === 0 && <div className="text-white/40 text-xs italic py-4 text-center">No sealed documents yet.</div>}
          </div>
        </div>
      )}

      {/* Dead Man's Switch */}
      <div className="mt-6 pt-6 border-t border-white/10" data-testid="dead-man-switch">
        <div className="flex items-center gap-2 mb-3">
          <Skull size={13} className="text-red-400/80"/>
          <span className="font-mono-uc text-[11px] uppercase tracking-widest text-red-400/90">Dead Man's Switch</span>
        </div>

        {dmStatus && (
          <div className="p-3 border border-white/5 bg-black/40 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 flex items-center gap-1"><Clock size={10}/> Days Remaining</span>
              <span className="font-serif-display text-2xl text-amber-400" data-testid="dm-days-remaining">
                {Math.floor(dmStatus.days_remaining)}
              </span>
            </div>
            <div className="w-full h-1 bg-white/10 relative">
              <div
                className="h-full bg-amber-400 transition-all"
                style={{ width: `${Math.min(100, (dmStatus.days_since_activity / dmStatus.threshold_days) * 100)}%` }}
              />
            </div>
            <div className="flex justify-between font-mono-uc text-[10px] text-white/40 mt-2">
              <span>Last active {relativeTime(new Date(Date.now() - dmStatus.days_since_activity * 86400000).toISOString())}</span>
              <span>Threshold {dmStatus.threshold_days}d</span>
            </div>
            <Button
              data-testid="dm-checkin-btn"
              onClick={checkIn}
              variant="ghost"
              className="w-full mt-3 h-8 rounded-none border border-white/10 text-white/70 hover:bg-white/5 font-mono-uc text-[10px] uppercase tracking-widest"
            >
              I'm Still Here — Reset Timer
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input
            data-testid="dm-contact-name"
            placeholder="Trusted Contact Name"
            value={contact.name}
            onChange={(e) => setContact({ ...contact, name: e.target.value })}
            className="h-9 rounded-none bg-black border-white/10 text-sm"
          />
          <Input
            data-testid="dm-contact-email"
            type="email"
            placeholder="contact@email.com"
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            className="h-9 rounded-none bg-black border-white/10 text-sm"
          />
          <div className="flex gap-2">
            <Input
              data-testid="dm-days"
              type="number"
              placeholder="Days"
              value={contact.days}
              onChange={(e) => setContact({ ...contact, days: e.target.value })}
              className="h-9 rounded-none bg-black border-white/10 text-sm font-mono-uc w-20"
            />
            <Button data-testid="dm-save-btn" onClick={saveDeadMan} className="uc-btn-amber rounded-none h-9 flex-1 text-xs">Save Switch</Button>
          </div>
        </div>

        <div>
          <label className="font-mono-uc text-[10px] uppercase tracking-widest text-red-300/80 block mb-1">
            Message to Beneficiary · Legacy Note
          </label>
          <textarea
            data-testid="dm-beneficiary-message"
            value={contact.beneficiary_message}
            onChange={(e) => setContact({ ...contact, beneficiary_message: e.target.value })}
            placeholder="This is the first thing your beneficiary sees when the portal opens. Write what you'd want them to hear."
            className="w-full min-h-[100px] rounded-none bg-black border border-white/10 p-2 text-sm text-white/80 focus:outline-none focus:border-red-500/40 font-mono-uc"
          />
          <div className="font-mono-uc text-[10px] text-white/40 mt-1">
            Delivered only when the Dead Man's Switch fires. Stored in-plain on the user record; consider what you write.
          </div>
        </div>
      </div>
    </section>
  );
}
